/**
 * Formats currency amounts and resolves value **color** for account / transaction rows.
 *
 * - **`isCredit` `null` / `undefined`:** display **`$X.XX`** with no sign; value uses default text color.
 * - **`isCredit` `true` (credit-card context):** negative amounts are prefixed with **-** and tinted **green** (payment);
 *   positive amounts are **`$X.XX`** with default color.
 * - **`isCredit` `false` (deposit / bank account):** positive amounts are **green** with no **+** prefix; negative amounts
 *   use a **-** prefix and **red** emphasis.
 */
/** Whole-dollar grouping for {@link CadsCreditValueStyle.formatted} (fixed locale so output is not runtime-dependent). */
const _usdWholePartFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export class CadsCreditValueStyle {
  constructor(private readonly _isCredit: boolean | null | undefined) {}

  /**
   * Returns a CSS `color` value (e.g. `var(--cads-color-green)`) when the amount should be emphasized, otherwise
   * **`null`** so the value inherits the surrounding text color.
   */
  colorFor(amount: number): string | null {
    if (this._isCredit == null) {
      return null;
    }
    return this._isCredit
      ? amount < 0
        ? 'var(--cads-color-green)'
        : null
      : amount >= 0
        ? 'var(--cads-color-green)'
        : 'var(--cads-color-red)';
  }

  /**
   * USD-style figures: when {@link CadsCreditValueStyle} is active (`isCredit` is boolean), negative amounts use a
   * leading **-**; positive amounts use **$** only (green emphasis is via {@link colorFor}, not a **+** prefix).
   */
  formatted(amount: number): string {
    const cents = Math.round(Math.abs(amount) * 100);
    const whole = _usdWholePartFormatter.format(Math.floor(cents / 100));
    const dec = (cents % 100).toString().padStart(2, '0');
    const d = '$' + whole + '.' + dec;
    if (this._isCredit == null) {
      return d;
    }
    return amount < 0 ? '-' + d : d;
  }
}
